import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AssessmentPhase,
  canMarkInAppAssessmentError,
  canSelectInAppAssessmentLastWord,
  DEFAULT_ASSESSMENT_DURATION_SECONDS,
  InAppAssessmentStats,
} from './in-app-assessment-utils';

/**
 * `idle` and `paused` differ only in whether the countdown has been touched: `idle` still shows the
 * full duration and disables "Finished early", matching the first mockup.
 */

export interface StudentAssessmentState {
  phase: AssessmentPhase;
  /** Token positions the coach marked as read incorrectly. */
  markedWordIndexes: number[];
  /** Token position of the last word read, picked after time runs out. */
  lastWordIndex: number | null;
  secondsRemaining: number;
  /** Set once submitted, so the completion banner can keep showing what was saved. */
  submittedStats: InAppAssessmentStats | null;
}

const TICK_INTERVAL_MS = 250;

function initialState(durationSeconds: number): StudentAssessmentState {
  return {
    phase: 'idle',
    markedWordIndexes: [],
    lastWordIndex: null,
    secondsRemaining: durationSeconds,
    submittedStats: null,
  };
}

/**
 * Owns per-student assessment state for the whole session, keyed by assessment result id, plus the
 * countdown for whichever student is selected.
 *
 * The countdown is derived from a monotonic timestamp rather than by decrementing on each tick, so a
 * throttled background tab cannot make the timer drift.
 */
export function useInAppAssessmentRunner({
  selectedAssessmentResultId,
  durationSeconds = DEFAULT_ASSESSMENT_DURATION_SECONDS,
}: {
  selectedAssessmentResultId: string | null;
  durationSeconds?: number;
}) {
  const [stateByResultId, setStateByResultId] = useState<Record<string, StudentAssessmentState>>({});

  /** While the countdown runs: when the current run started, and the seconds left at that point. */
  const runStartRef = useRef<{ startedAt: number; secondsRemainingAtStart: number } | null>(null);

  const selectedState = selectedAssessmentResultId
    ? stateByResultId[selectedAssessmentResultId] ?? initialState(durationSeconds)
    : initialState(durationSeconds);

  const updateSelected = useCallback(
    (update: (current: StudentAssessmentState) => StudentAssessmentState) => {
      if (!selectedAssessmentResultId) {
        return;
      }
      setStateByResultId((current) => {
        const existing = current[selectedAssessmentResultId] ?? initialState(durationSeconds);
        return { ...current, [selectedAssessmentResultId]: update(existing) };
      });
    },
    [selectedAssessmentResultId, durationSeconds],
  );

  const start = useCallback(() => {
    if (selectedState.secondsRemaining <= 0) {
      return;
    }
    runStartRef.current = {
      startedAt: performance.now(),
      secondsRemainingAtStart: selectedState.secondsRemaining,
    };
    updateSelected((current) => ({ ...current, phase: 'running' }));
  }, [selectedState.secondsRemaining, updateSelected]);

  const pause = useCallback(() => {
    runStartRef.current = null;
    updateSelected((current) => (current.phase === 'running' ? { ...current, phase: 'paused' } : current));
  }, [updateSelected]);

  /** Keeps `submittedStats` so a re-run does not make an already saved result look unstarted. */
  const reset = useCallback(() => {
    runStartRef.current = null;
    updateSelected((current) => ({ ...initialState(durationSeconds), submittedStats: current.submittedStats }));
  }, [updateSelected, durationSeconds]);

  const requestEarlyFinish = useCallback(() => {
    runStartRef.current = null;
    updateSelected((current) =>
      current.phase === 'running' ? { ...current, phase: 'confirmingEarlyFinish' } : current,
    );
  }, [updateSelected]);

  const cancelEarlyFinish = useCallback(() => {
    updateSelected((current) =>
      current.phase === 'confirmingEarlyFinish' ? { ...current, phase: 'paused' } : current,
    );
  }, [updateSelected]);

  const confirmEarlyFinish = useCallback(() => {
    updateSelected((current) =>
      current.phase === 'confirmingEarlyFinish' ? { ...current, phase: 'earlyFinishReady' } : current,
    );
  }, [updateSelected]);

  const toggleMarkedWord = useCallback(
    (wordIndex: number) => {
      updateSelected((current) => {
        const alreadyMarked = current.markedWordIndexes.includes(wordIndex);
        if (!alreadyMarked && !canMarkInAppAssessmentError(wordIndex, current.lastWordIndex)) {
          return current;
        }
        return {
          ...current,
          markedWordIndexes: alreadyMarked
            ? current.markedWordIndexes.filter((index) => index !== wordIndex)
            : [...current.markedWordIndexes, wordIndex].toSorted((a, b) => a - b),
        };
      });
    },
    [updateSelected],
  );

  const selectLastWord = useCallback(
    (wordIndex: number) => {
      updateSelected((current) => {
        if (!canSelectInAppAssessmentLastWord(wordIndex, current.markedWordIndexes)) {
          return current;
        }
        return { ...current, lastWordIndex: wordIndex };
      });
    },
    [updateSelected],
  );

  const markSubmitting = useCallback(() => {
    runStartRef.current = null;
    updateSelected((current) => ({ ...current, phase: 'submitting' }));
  }, [updateSelected]);

  const markSubmitted = useCallback(
    (stats: InAppAssessmentStats) => {
      updateSelected((current) => ({ ...current, phase: 'done', submittedStats: stats }));
    },
    [updateSelected],
  );

  const revertPhase = useCallback(
    (phase: AssessmentPhase) => {
      updateSelected((current) => ({ ...current, phase }));
    },
    [updateSelected],
  );

  // Drives the countdown for the selected student while it is running.
  useEffect(() => {
    if (!selectedAssessmentResultId || selectedState.phase !== 'running') {
      return;
    }

    const interval = setInterval(() => {
      const run = runStartRef.current;
      if (!run) {
        return;
      }
      const elapsedSeconds = (performance.now() - run.startedAt) / 1000;
      const remaining = Math.max(0, Math.ceil(run.secondsRemainingAtStart - elapsedSeconds));

      if (remaining <= 0) {
        runStartRef.current = null;
      }

      setStateByResultId((current) => {
        const existing = current[selectedAssessmentResultId];
        if (!existing || existing.phase !== 'running' || existing.secondsRemaining === remaining) {
          return current;
        }
        return {
          ...current,
          [selectedAssessmentResultId]: {
            ...existing,
            secondsRemaining: remaining,
            phase: remaining <= 0 ? 'timeUp' : 'running',
          },
        };
      });
    }, TICK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [selectedAssessmentResultId, selectedState.phase]);

  return {
    selectedState,
    stateByResultId,
    start,
    pause,
    reset,
    requestEarlyFinish,
    cancelEarlyFinish,
    confirmEarlyFinish,
    toggleMarkedWord,
    selectLastWord,
    markSubmitting,
    markSubmitted,
    revertPhase,
  };
}
